/**
 * Locale settings for the multilingual storefront (29 locales, including distinct pt and pt-br
 * and the regional es-mx, see src/config/regional-locales.mjs).
 *
 * Each locale maps to a BCP 47 tag, Saleor language code, and display metadata.
 * Currency comes from Saleor channels, not here.
 */

export const locales = [
	"en",
	"de",
	"it",
	"es",
	"es-mx",
	"fr",
	"nl",
	"pt",
	"pt-br",
	"pl",
	"sv",
	"da",
	"el",
	"cs",
	"fi",
	"ro",
	"hu",
	"sk",
	"tr",
	"nb",
	"bg",
	"hr",
	"sl",
	"sr",
	"bs",
	"mk",
	"lt",
	"lv",
	"et",
] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = "de";

import { LanguageCodeEnum } from "@/gql/graphql";
import { REGIONAL_LOCALES } from "./regional-locales.mjs";
import { US_INDEXABLE_LOCALES } from "./us-indexing.mjs";

export interface LocaleConfig {
	/** BCP 47 locale for Intl APIs (number/date formatting) */
	bcp47: string;
	/**
	 * Saleor GraphQL LanguageCodeEnum value: the locale's identity (checkout, order and account
	 * language). Content reads use `getSaleorContentLanguageCode`, see saleor-language-code-usage.test.ts.
	 */
	saleorLanguageCode: LanguageCodeEnum;
	/** HTML lang attribute */
	htmlLang: string;
	/** Open Graph locale */
	ogLocale: string;
	/** Native name for language switcher */
	nativeName: string;
	/** Fallback currency when API returns null */
	fallbackCurrency: string;
}

const localeConfigs: Record<Locale, LocaleConfig> = {
	en: {
		bcp47: "en-US",
		saleorLanguageCode: LanguageCodeEnum.EnUs,
		htmlLang: "en",
		ogLocale: "en_US",
		nativeName: "English",
		fallbackCurrency: "USD",
	},
	de: {
		bcp47: "de-DE",
		saleorLanguageCode: LanguageCodeEnum.De,
		htmlLang: "de",
		ogLocale: "de_DE",
		nativeName: "Deutsch",
		fallbackCurrency: "EUR",
	},
	it: {
		bcp47: "it-IT",
		saleorLanguageCode: LanguageCodeEnum.ItIt,
		htmlLang: "it",
		ogLocale: "it_IT",
		nativeName: "Italiano",
		fallbackCurrency: "EUR",
	},
	es: {
		bcp47: "es-ES",
		saleorLanguageCode: LanguageCodeEnum.EsEs,
		htmlLang: "es",
		ogLocale: "es_ES",
		nativeName: "Español",
		fallbackCurrency: "EUR",
	},
	// Regional locale of `es`. ES_MX is its identity code (checkout, orders, mails, scripts);
	// getSaleorContentLanguageCode maps its content reads to ES_ES.
	"es-mx": {
		bcp47: "es-MX",
		saleorLanguageCode: LanguageCodeEnum.EsMx,
		htmlLang: "es-MX",
		ogLocale: "es_MX",
		nativeName: "Español (México)",
		fallbackCurrency: "USD",
	},
	fr: {
		bcp47: "fr-FR",
		saleorLanguageCode: LanguageCodeEnum.FrFr,
		htmlLang: "fr",
		ogLocale: "fr_FR",
		nativeName: "Français",
		fallbackCurrency: "EUR",
	},
	nl: {
		bcp47: "nl-NL",
		saleorLanguageCode: LanguageCodeEnum.NlNl,
		htmlLang: "nl",
		ogLocale: "nl_NL",
		nativeName: "Nederlands",
		fallbackCurrency: "EUR",
	},
	pt: {
		bcp47: "pt-PT",
		saleorLanguageCode: LanguageCodeEnum.PtPt,
		htmlLang: "pt",
		ogLocale: "pt_PT",
		nativeName: "Português",
		fallbackCurrency: "EUR",
	},
	"pt-br": {
		bcp47: "pt-BR",
		saleorLanguageCode: LanguageCodeEnum.PtBr,
		htmlLang: "pt-BR",
		ogLocale: "pt_BR",
		nativeName: "Português (Brasil)",
		fallbackCurrency: "EUR",
	},
	pl: {
		bcp47: "pl-PL",
		saleorLanguageCode: LanguageCodeEnum.PlPl,
		htmlLang: "pl",
		ogLocale: "pl_PL",
		nativeName: "Polski",
		fallbackCurrency: "EUR",
	},
	sv: {
		bcp47: "sv-SE",
		saleorLanguageCode: LanguageCodeEnum.SvSe,
		htmlLang: "sv",
		ogLocale: "sv_SE",
		nativeName: "Svenska",
		fallbackCurrency: "EUR",
	},
	da: {
		bcp47: "da-DK",
		saleorLanguageCode: LanguageCodeEnum.DaDk,
		htmlLang: "da",
		ogLocale: "da_DK",
		nativeName: "Dansk",
		fallbackCurrency: "EUR",
	},
	el: {
		bcp47: "el-GR",
		saleorLanguageCode: LanguageCodeEnum.ElGr,
		htmlLang: "el",
		ogLocale: "el_GR",
		nativeName: "Ελληνικά",
		fallbackCurrency: "EUR",
	},
	cs: {
		bcp47: "cs-CZ",
		saleorLanguageCode: LanguageCodeEnum.CsCz,
		htmlLang: "cs",
		ogLocale: "cs_CZ",
		nativeName: "Čeština",
		fallbackCurrency: "EUR",
	},
	fi: {
		bcp47: "fi-FI",
		saleorLanguageCode: LanguageCodeEnum.FiFi,
		htmlLang: "fi",
		ogLocale: "fi_FI",
		nativeName: "Suomi",
		fallbackCurrency: "EUR",
	},
	ro: {
		bcp47: "ro-RO",
		saleorLanguageCode: LanguageCodeEnum.RoRo,
		htmlLang: "ro",
		ogLocale: "ro_RO",
		nativeName: "Română",
		fallbackCurrency: "EUR",
	},
	hu: {
		bcp47: "hu-HU",
		saleorLanguageCode: LanguageCodeEnum.HuHu,
		htmlLang: "hu",
		ogLocale: "hu_HU",
		nativeName: "Magyar",
		fallbackCurrency: "EUR",
	},
	sk: {
		bcp47: "sk-SK",
		saleorLanguageCode: LanguageCodeEnum.SkSk,
		htmlLang: "sk",
		ogLocale: "sk_SK",
		nativeName: "Slovenčina",
		fallbackCurrency: "EUR",
	},
	tr: {
		bcp47: "tr-TR",
		saleorLanguageCode: LanguageCodeEnum.TrTr,
		htmlLang: "tr",
		ogLocale: "tr_TR",
		nativeName: "Türkçe",
		fallbackCurrency: "EUR",
	},
	nb: {
		bcp47: "nb-NO",
		saleorLanguageCode: LanguageCodeEnum.NbNo,
		htmlLang: "nb",
		ogLocale: "nb_NO",
		nativeName: "Norsk bokmål",
		fallbackCurrency: "EUR",
	},
	bg: {
		bcp47: "bg-BG",
		saleorLanguageCode: LanguageCodeEnum.BgBg,
		htmlLang: "bg",
		ogLocale: "bg_BG",
		nativeName: "Български",
		fallbackCurrency: "EUR",
	},
	hr: {
		bcp47: "hr-HR",
		saleorLanguageCode: LanguageCodeEnum.HrHr,
		htmlLang: "hr",
		ogLocale: "hr_HR",
		nativeName: "Hrvatski",
		fallbackCurrency: "EUR",
	},
	sl: {
		bcp47: "sl-SI",
		saleorLanguageCode: LanguageCodeEnum.SlSi,
		htmlLang: "sl",
		ogLocale: "sl_SI",
		nativeName: "Slovenščina",
		fallbackCurrency: "EUR",
	},
	// Serbian is served in Latin script (decision Welle 1), not Cyrillic.
	sr: {
		bcp47: "sr-Latn-RS",
		saleorLanguageCode: LanguageCodeEnum.SrLatnRs,
		htmlLang: "sr-Latn",
		ogLocale: "sr_RS",
		nativeName: "Srpski",
		fallbackCurrency: "EUR",
	},
	bs: {
		bcp47: "bs-BA",
		saleorLanguageCode: LanguageCodeEnum.BsLatn,
		htmlLang: "bs",
		ogLocale: "bs_BA",
		nativeName: "Bosanski",
		fallbackCurrency: "EUR",
	},
	mk: {
		bcp47: "mk-MK",
		saleorLanguageCode: LanguageCodeEnum.MkMk,
		htmlLang: "mk",
		ogLocale: "mk_MK",
		nativeName: "Македонски",
		fallbackCurrency: "EUR",
	},
	lt: {
		bcp47: "lt-LT",
		saleorLanguageCode: LanguageCodeEnum.LtLt,
		htmlLang: "lt",
		ogLocale: "lt_LT",
		nativeName: "Lietuvių",
		fallbackCurrency: "EUR",
	},
	lv: {
		bcp47: "lv-LV",
		saleorLanguageCode: LanguageCodeEnum.LvLv,
		htmlLang: "lv",
		ogLocale: "lv_LV",
		nativeName: "Latviešu",
		fallbackCurrency: "EUR",
	},
	et: {
		bcp47: "et-EE",
		saleorLanguageCode: LanguageCodeEnum.EtEe,
		htmlLang: "et",
		ogLocale: "et_EE",
		nativeName: "Eesti",
		fallbackCurrency: "EUR",
	},
};

/**
 * Get locale configuration by locale code.
 * Falls back to default locale if invalid.
 */
export function getLocaleConfig(locale: string): LocaleConfig {
	if (locale in localeConfigs) {
		return localeConfigs[locale as Locale];
	}
	return localeConfigs[defaultLocale];
}

/** Check if a string is a valid locale */
export function isValidLocale(value: string): value is Locale {
	return locales.includes(value as Locale);
}

/**
 * Map a Saleor `LanguageCodeEnum` value (or any BCP 47 tag) onto a storefront locale.
 *
 * Saleors Codes sind laenderkodiert (FR_FR, EN_US, PT_BR), die Storefront-Routen
 * sind es nicht, mit den Ausnahmen `pt-br` und `es-mx`, die eigene Locales sind.
 * Deshalb wird zuerst das vollstaendige Tag geprueft, damit PT_BR nie zu `pt`
 * und ES_MX nie zu `es` kollabiert, und erst danach die Basissprache. Dieselbe
 * Reihenfolge wie `detectLocale` in middleware.ts.
 *
 * Umkehrfunktion zu `getLocaleConfig(locale).saleorLanguageCode`: fuer alle 29
 * Locales ist der Roundtrip verlustfrei, siehe Test in locale.test.ts.
 *
 * Die Funktion ist total: Ein Code, den wir nicht uebersetzen, wird `en` und
 * niemals null. Englisch ist die neutrale Wahl. `defaultLocale` waere Deutsch und
 * damit fuer jeden nicht-deutschen Leser falsch, und ein null-Rueckgabewert laesst
 * die Nachricht still ausfallen. Genau das hat am 08.09.2026 die Recovery-Queue
 * angehalten.
 *
 * Bewusst NICHT mit `resolveEmailLocale` (src/lib/email/newsletter-templates.ts)
 * oder `resolveCampaignLocale` (src/lib/campaigns/locale-resolve.ts) zusammenfuehren:
 * deren Wertebereich schliesst `pt-br` aus. Eine Fusion wuerde brasilianische
 * Recovery-Links von `/pt-br/checkout/recover` auf `/pt/checkout/recover` kollabieren.
 */
export function resolveLocaleFromLanguageCode(value: string | null | undefined): Locale {
	const tag = (value ?? "").trim().toLowerCase().replaceAll("_", "-");
	if (isValidLocale(tag)) return tag;
	const base = tag.split("-")[0];
	return isValidLocale(base) ? base : "en";
}

/**
 * Read the next-intl locale out of a storefront pathname like `/de/eu/products/ss-31`.
 * Returns null for anything that is not a known locale segment.
 */
export function parseLocaleFromPathname(pathname: string | null | undefined): Locale | null {
	if (!pathname) return null;
	const segment = pathname.split("/")[1];
	return segment && isValidLocale(segment) ? segment : null;
}

/**
 * Known channels for hreflang generation in HTML metadata and sitemaps.
 * Static list because metadata generation cannot make API calls.
 * If a new channel is added, update this array AND NEXT_PUBLIC_SITEMAP_CHANNELS.
 */
export const sitemapChannels = ["eu", "us"] as const;
export type SitemapChannel = (typeof sitemapChannels)[number];

/**
 * Primary SEO target per locale.
 *
 * We keep the generic language code (for example `it`, `es`, `de`) on the
 * primary market URL and use a region-specific variant for secondary markets.
 * This prevents channel collisions in hreflang output and keeps EU pages as
 * the main search target for all storefront locales except regional ones,
 * whose primary channel comes from src/config/regional-locales.mjs.
 */
const primaryHreflangChannelByLocale: Record<Locale, SitemapChannel> = {
	en: "eu",
	de: "eu",
	it: "eu",
	es: "eu",
	"es-mx": REGIONAL_LOCALES["es-mx"].primaryChannel,
	fr: "eu",
	nl: "eu",
	pt: "eu",
	"pt-br": "eu",
	pl: "eu",
	sv: "eu",
	da: "eu",
	el: "eu",
	cs: "eu",
	fi: "eu",
	ro: "eu",
	hu: "eu",
	sk: "eu",
	tr: "eu",
	nb: "eu",
	bg: "eu",
	hr: "eu",
	sl: "eu",
	sr: "eu",
	bs: "eu",
	mk: "eu",
	lt: "eu",
	lv: "eu",
	et: "eu",
};

export function getPrimaryHreflangChannel(locale: Locale): SitemapChannel {
	return primaryHreflangChannelByLocale[locale];
}

/**
 * Locales that stay search-indexable on the US channel.
 *
 * The US channel is English-first with a sizeable Hispanic audience, and the
 * home channel of es-mx. Every other locale on `/us` (e.g. `/de/us`, `/fr/us`)
 * is a near-duplicate of its `/eu` counterpart with no search demand — indexing
 * them only dilutes crawl budget. They stay reachable but are excluded from
 * indexing, the sitemap and hreflang clusters. The EU channel indexes every
 * locale except regional ones outside their indexable channels (es-mx).
 */
const usIndexableLocales: readonly Locale[] = US_INDEXABLE_LOCALES;

/**
 * Whether a locale+channel URL should be indexed and listed in the sitemap and
 * hreflang clusters. Regional locales follow their `indexableChannels` (es-mx:
 * only US); otherwise EU indexes all locales and US only EN and ES.
 */
export function isIndexableLocaleChannel(locale: Locale, channel: string): boolean {
	const normalizedChannel = channel.toLowerCase();
	const regional = getRegionalSpec(locale);
	if (regional) {
		return regional.indexableChannels.some((indexable) => indexable === normalizedChannel);
	}
	if (normalizedChannel === "us") {
		return usIndexableLocales.includes(locale);
	}
	return true;
}

/** One row of src/config/regional-locales.mjs, typed against the storefront locale model. */
interface RegionalLocaleSpec {
	/** Locale whose editorial content the regional locale falls back to. */
	base: Locale;
	/** Search target and destination of a bare `/<locale>` path. */
	primaryChannel: SitemapChannel;
	/** Channels on which the locale is indexed; noindex on every other channel. */
	indexableChannels: readonly SitemapChannel[];
	/** Country that selects the locale on entry with the base language or no browser language. */
	country: string;
	/** Reference display currency without a country signal. */
	referenceCurrency: string;
	/** Home country in checkout. */
	checkoutCountry: string;
	/** IANA zone for mail send windows. */
	timeZone: string;
}

const regionalLocaleSpecs: Readonly<Partial<Record<Locale, Readonly<RegionalLocaleSpec>>>> = REGIONAL_LOCALES;

/** The regional table entry of a locale, or null for every non-regional locale. */
export function getRegionalSpec(locale: Locale): Readonly<RegionalLocaleSpec> | null {
	return Object.hasOwn(regionalLocaleSpecs, locale) ? (regionalLocaleSpecs[locale] ?? null) : null;
}

/** Whether a locale is regional: own messages and market, editorial content from its base locale. */
export function isRegionalLocale(locale: Locale): boolean {
	return getRegionalSpec(locale) !== null;
}

/** Base locale for editorial content: `es` for es-mx, the locale itself otherwise. */
export function getBaseLocale(locale: Locale): Locale {
	return getRegionalSpec(locale)?.base ?? locale;
}

/** Locales to try for editorial content, most specific first: `["es-mx", "es"]`, else `[locale]`. */
export function getContentFallbackChain(locale: Locale): readonly Locale[] {
	const base = getRegionalSpec(locale)?.base;
	return base ? [locale, base] : [locale];
}

/**
 * Regional locale chosen on entry for a visitor from `country` whose language resolved to the
 * regional locale's base (MX with `es` or `es-mx` → `es-mx`). Null for every other combination.
 */
export function getRegionalLocaleForCountry(
	country: string | null | undefined,
	resolvedLocale: string,
): Locale | null {
	const normalizedCountry = country?.trim().toUpperCase();
	if (!normalizedCountry) return null;
	const normalizedLocale = resolvedLocale.trim().toLowerCase();
	const resolvedBase = getBaseLocaleOf(normalizedLocale);
	return (
		locales.find((locale) => {
			const spec = getRegionalSpec(locale);
			return spec?.country === normalizedCountry && spec.base === resolvedBase;
		}) ?? null
	);
}

/**
 * `getBaseLocale` for an unchecked locale string (route params, table keys): es-mx → es, every
 * other locale itself, and a string outside `locales` unchanged.
 */
export function getBaseLocaleOf(locale: string): string {
	return isValidLocale(locale) ? getBaseLocale(locale) : locale;
}

/**
 * `getContentFallbackChain` for an unchecked locale string: `["es-mx", "es"]` for es-mx,
 * `[locale]` for every other value, including strings outside `locales`.
 */
export function getContentFallbackChainOf(locale: string): readonly string[] {
	return isValidLocale(locale) ? getContentFallbackChain(locale) : [locale];
}

/**
 * Saleor code for content reads (products, categories, pages, menus, search). Saleor returns a
 * translation only for the exact code asked for, so a regional locale reads its base translation
 * (es-mx → ES_ES). Every other locale reads its identity code `saleorLanguageCode`.
 */
export function getSaleorContentLanguageCode(locale: string): LanguageCodeEnum {
	return getLocaleConfig(getBaseLocaleOf(locale)).saleorLanguageCode;
}

/**
 * Saleor code of a regional locale's own translation (es-mx → ES_MX), read next to the content
 * code where a surface can carry own regional text (guide and comparison detail routes). Null for
 * every other locale, which reads only `getSaleorContentLanguageCode`.
 */
export function getSaleorRegionalLanguageCode(locale: string): LanguageCodeEnum | null {
	return isValidLocale(locale) && isRegionalLocale(locale)
		? getLocaleConfig(locale).saleorLanguageCode
		: null;
}

/**
 * Default member set of hreflang clusters, sitemap entries and the feed: every locale except the
 * regional ones. A regional locale joins a cluster only where its surface reports own content.
 */
export const hreflangDefaultLocales: readonly Locale[] = Object.freeze(
	locales.filter((locale) => !isRegionalLocale(locale)),
);

/**
 * Canonical path of a regional page without own content: the same path on the base locale's
 * primary channel (es-mx → `/es/eu<pathSuffix>`).
 */
export function getRegionalFallbackCanonicalPath(locale: Locale, pathSuffix: string): string {
	const base = getBaseLocale(locale);
	return `/${base}/${getPrimaryHreflangChannel(base)}${pathSuffix}`;
}

/**
 * Language tags for locales whose bare code names the wrong script: `sr` alone
 * means Cyrillic in BCP 47, the storefront serves Serbian in Latin script.
 */
const SCRIPT_LANGUAGE_TAGS: Partial<Record<Locale, string>> = { sr: "sr-Latn" };

/**
 * Get the hreflang code for a locale+channel combination.
 *
 * Examples:
 * - `it` on EU -> `it`
 * - `it` on US -> `it-US`
 * - `en` on EU -> `en`
 * - `en` on US -> `en-US`
 * - `es-mx` on US (its primary channel) -> `es-mx`
 *
 * `hreflang` models search targeting, not internal sales-channel semantics.
 * The US channel therefore gets a region-specific suffix while the primary
 * locale target keeps the bare language code.
 */
export function getHreflangCode(locale: Locale, channel: string): string {
	const normalizedChannel = channel.toLowerCase();
	const language = SCRIPT_LANGUAGE_TAGS[locale] ?? locale;

	if (normalizedChannel === getPrimaryHreflangChannel(locale)) {
		return language;
	}

	if (normalizedChannel === "us") {
		return `${language}-US`;
	}

	return language;
}

/**
 * BCP 47 language tag of the content behind a locale/channel URL, derived from the hreflang
 * target: bare language on the primary channel (`de`, `en`), `pt-BR` and `es-MX` for the
 * region-coded locales, `xx-US` on the US channel. Used for every JSON-LD `inLanguage`.
 */
export function getContentLanguageTag(locale: Locale, channel: string): string {
	if (locale.includes("-")) {
		const [language, region] = locale.split("-");
		return `${language}-${region.toUpperCase()}`;
	}
	return getHreflangCode(locale, channel);
}

/**
 * Normalize Unicode whitespace from Intl.NumberFormat output.
 *
 * Node.js and browsers use different whitespace between number and currency symbol:
 * - Node.js: NBSP (\u00A0)
 * - iOS Safari 17+: NNBSP (\u202F, narrow no-break space)
 *
 * This mismatch causes React hydration errors (#418 text). Normalizing to NBSP
 * ensures consistent output and preserves the no-break behavior.
 */
function normalizeIntlWhitespace(str: string): string {
	return str.replace(/\u202F/g, "\u00A0");
}

/**
 * Normalize a Saleor / API currency code for Intl (must be 3 letters; empty or invalid → fallback).
 */
function resolveCurrencyForIntl(raw: string | null | undefined, fallback: string): string {
	if (typeof raw !== "string") return fallback;
	const trimmed = raw.trim();
	return /^[A-Za-z]{3}$/.test(trimmed) ? trimmed.toUpperCase() : fallback;
}

/**
 * Internal support surfaces also use Simplified Chinese. This formatting-only
 * extension does not add a storefront route, Saleor language or currency policy.
 */
type FormattingLocale = Locale | "zh-CN";

function getFormattingLocaleConfig(
	locale: FormattingLocale,
): Pick<LocaleConfig, "bcp47" | "fallbackCurrency"> {
	return locale === "zh-CN" ? { bcp47: "zh-CN", fallbackCurrency: "EUR" } : getLocaleConfig(locale);
}

/**
 * Dollar-named currencies whose home-locale symbol is a bare "$" (MXN in `es-MX`), which
 * next to the USD channel price would read as US dollars. Only that exact symbol is
 * replaced; unambiguous symbols such as `MX$`, `$MX` or `MXN` stay as Intl renders them.
 */
const DISAMBIGUATED_DOLLAR_SYMBOLS: Readonly<Record<string, string>> = {
	MXN: "MX$",
};

/**
 * Format a price with the given locale.
 *
 * Saleor should always send a valid ISO 4217 code; when it does not, `Intl.NumberFormat`
 * throws (e.g. empty string) and would collapse entire client subtrees behind an error boundary.
 */
export function formatPrice(
	amount: number,
	currency: string,
	locale: FormattingLocale = defaultLocale,
	options?: { minimumFractionDigits?: number; maximumFractionDigits?: number },
): string {
	const config = getFormattingLocaleConfig(locale);
	const safeAmount = Number.isFinite(amount) ? amount : 0;
	const code = resolveCurrencyForIntl(currency, config.fallbackCurrency);
	try {
		const formatter = new Intl.NumberFormat(config.bcp47, {
			style: "currency",
			currency: code,
			...options,
		});
		const symbol = DISAMBIGUATED_DOLLAR_SYMBOLS[code];
		const formatted = symbol
			? formatter
					.formatToParts(safeAmount)
					.map((part) => (part.type === "currency" && part.value === "$" ? symbol : part.value))
					.join("")
			: formatter.format(safeAmount);
		return normalizeIntlWhitespace(formatted);
	} catch {
		return `${safeAmount} ${code}`;
	}
}

const dateComponentKeys = [
	"weekday",
	"era",
	"year",
	"month",
	"day",
	"dayPeriod",
	"hour",
	"minute",
	"second",
	"fractionalSecondDigits",
	"timeZoneName",
] as const;

/**
 * Format a date with the given locale. Uses the BCP-47 tag from the locale config
 * (e.g. `sr-Latn-RS` for `sr`, so months come out in Latin script).
 *
 * Without options, `dateStyle: "medium"` applies. Explicit component options
 * (`day`, `month`, `year`, ...) replace that default, because Intl rejects
 * `dateStyle` combined with component fields.
 */
export function formatDate(
	date: Date | number,
	options?: Intl.DateTimeFormatOptions,
	locale: FormattingLocale = defaultLocale,
): string {
	const hasComponents = options !== undefined && dateComponentKeys.some((key) => options[key] !== undefined);
	return new Intl.DateTimeFormat(getFormattingLocaleConfig(locale).bcp47, {
		...(hasComponents ? {} : { dateStyle: "medium" as const }),
		...options,
	}).format(date);
}
